import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import CategoriesService from '../services/categoriesService';
import PubliciteService from '../services/publiciteService';
import CONFIG from '../../globalVariables.js';

const Publicite = ({ useIds = false, usePubliciteId = false }) => {
  const { ville, departement, categorie, villeId, departementId, categorieId, publiciteId } = useParams();
  const [publiciteData, setPubliciteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [slidesPerView, setSlidesPerView] = useState(1);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [reasonsSlidesPerView, setReasonsSlidesPerView] = useState(1);
  const [reasonsIndex, setReasonsIndex] = useState(0);
  const [categorySlug, setCategorySlug] = useState('');
  const [categoryTitle, setCategoryTitle] = useState('');
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryDescriptions, setGalleryDescriptions] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(true);

  // Déterminer le type et la valeur de la localisation
  const locationType = useIds ? (villeId ? 'ville' : 'departement') : (ville ? 'ville' : 'departement');
  const locationValue = useIds ? (villeId || departementId) : (ville || departement);
  
  // Valeurs par défaut si pas de paramètres dans l'URL
  const locationParam = locationValue || "Paris 16e";
  const categorieParam = useIds ? (categorieId || "") : (categorie || "Peinture");

  useEffect(() => {
    const loadPubliciteData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('🔍 DEBUG:', { usePubliciteId, publiciteId, useIds, ville, departement, categorie, villeId, departementId, categorieId });
        
        // Si on a un publiciteId, appeler directement par ID
        if (usePubliciteId && publiciteId) {
          console.log(`🔍 Chargement publicité par ID: ${publiciteId}`);
          const data = await PubliciteService.getPubliciteById(publiciteId);
          console.log('✅ Data reçue:', data);
          setPubliciteData(data);
          return;
        }
        
        console.log(`🔍 Chargement publicité: ${locationType} ${locationParam} - ${categorieParam} (useIds: ${useIds})`);
        
        // Appeler la bonne méthode selon le type et si on utilise des IDs
        let data;
        if (useIds) {
          // Appel par IDs
          data = locationType === 'ville' 
            ? await PubliciteService.getPubliciteVilleById(locationParam, categorieParam)
            : await PubliciteService.getPubliciteDepartementById(locationParam, categorieParam);
        } else {
          // Appel par noms
          data = locationType === 'ville' 
          ? await PubliciteService.getPubliciteVille(locationParam, categorieParam)
          : await PubliciteService.getPubliciteDepartement(locationParam, categorieParam);
        }
          
        console.log('✅ Données publicité chargées:', data);
        
        setPubliciteData(data);
        
      } catch (err) {
        console.error('🚨 Erreur chargement publicité:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadPubliciteData();
  }, [locationType, locationParam, categorieParam, useIds, usePubliciteId, publiciteId]);

  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      let spv = 1;
      if (w >= 1280) spv = 4; else if (w >= 1024) spv = 3; else if (w >= 640) spv = 2; else spv = 1;
      setSlidesPerView(spv);
      const rspv = w >= 640 ? 2 : 1;
      setReasonsSlidesPerView(rspv);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  useEffect(() => {
    try {
      const existingIcon = document.querySelector('link[rel="icon"], link[rel="shortcut icon"]');
      const expectedHref = '/favicon.ico';
      if (!existingIcon) {
        const link = document.createElement('link');
        link.rel = 'icon';
        link.href = expectedHref;
        link.type = 'image/x-icon';
        document.head.appendChild(link);
      } else {
        const href = existingIcon.getAttribute('href') || '';
        if (!/favicon\.ico/.test(href)) {
          existingIcon.setAttribute('href', expectedHref);
          existingIcon.setAttribute('type', 'image/x-icon');
        }
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    setCurrentIndex(0);
  }, [slidesPerView, galleryImages.length]);

  useEffect(() => {
    setReasonsIndex(0);
  }, [reasonsSlidesPerView, publiciteData?.texte?.pourquoi_choisir?.raisons?.length]);

  // useEffect pour gérer les métadonnées SEO
  useEffect(() => {
    if (publiciteData) {
      // Définir le titre de la page (100% basé sur contenus dynamiques)
      const dynamicTitle = publiciteData.texte?.meta_seo?.meta_title
        || publiciteData.texte?.hero?.titre_principal
        || '2RENOV - Rénovation et travaux';
      document.title = dynamicTitle;

      // Gérer la meta description
      const metaDescription = document.querySelector('meta[name="description"]');
      const descriptionContent = publiciteData.texte?.meta_seo?.meta_description 
        || (publiciteData.texte?.hero?.description ? (publiciteData.texte.hero.description.substring(0, 157) + '...') : '')
        || 'Spécialiste en rénovation et travaux. Devis gratuit.';

      if (metaDescription) {
        metaDescription.setAttribute('content', descriptionContent);
      } else {
        const newMetaDescription = document.createElement('meta');
        newMetaDescription.name = 'description';
        newMetaDescription.content = descriptionContent;
        document.head.appendChild(newMetaDescription);
      }
    }

    // Nettoyage à la destruction du composant
    return () => {
      document.title = '2RENOV - Rénovation et travaux';
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', 'Spécialiste en rénovation et travaux. Devis gratuit.');
      }
    };
  }, [publiciteData, locationParam, categorieParam]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const catId = publiciteData?.categorie?._id || publiciteData?.categorie || null;
        let slugName = categorieParam || '';
        let titleName = categorieParam || '';
        if (catId) {
          try {
            const all = await CategoriesService.getAllCategories();
            const found = all.find(c => String(c._id) === String(catId));
            if (found?.nom) {
              slugName = found.nom;
              titleName = found.nom;
            }
          } catch (_) {}
        }
        const s = String(slugName)
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
        if (alive) setCategorySlug(s);
        if (alive) setCategoryTitle(titleName);
      } catch (_) {}
    })();
    return () => { alive = false; };
  }, [publiciteData, categorieParam]);

  useEffect(() => {
    let alive = true;
    setGalleryLoading(true);
    (async () => {
      try {
        const res = await fetch(`${CONFIG.PATH_API}/api/content/templates?type=category`);
        if (!res.ok) return;
        const json = await res.json();
        const templates = Array.isArray(json.data) ? json.data : [];
        const norm = (s) => String(s || '')
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
        const target = norm(categorieParam);
        const tpl = templates.find(t => norm(t?.categorie?.nom || t?.categorie) === target);
        const gal = tpl?.texte?.galerie || {};
        const imgs = Array.isArray(gal.images) ? gal.images : [];
        const descs = Array.isArray(gal.descriptions) ? gal.descriptions : [];
        if (alive && imgs.length) {
          setGalleryImages(imgs);
          setGalleryDescriptions(descs);
        }
        if (alive) setGalleryLoading(false);
      } catch (_) {
        if (alive) setGalleryLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [categorieParam]);

  useEffect(() => {
    try {
      const urls = [];
      const hero = publiciteData?.texte?.hero?.background_image;
      if (hero) urls.push(resolveImageUrl(hero));
      if (Array.isArray(galleryImages)) {
        galleryImages.forEach((u) => urls.push(resolveImageUrl(u)));
      }
      const origins = Array.from(new Set(urls.map((u) => {
        try { return new URL(u).origin; } catch { return null; }
      }).filter(Boolean)));

      const links = [];
      const dns = [];
      origins.forEach((origin) => {
        const pre = document.createElement('link');
        pre.rel = 'preconnect';
        pre.href = origin;
        pre.crossOrigin = 'anonymous';
        document.head.appendChild(pre);
        links.push(pre);

        const df = document.createElement('link');
        df.rel = 'dns-prefetch';
        df.href = origin;
        document.head.appendChild(df);
        dns.push(df);
      });

      return () => {
        [...links, ...dns].forEach((l) => { if (l && l.parentNode) l.parentNode.removeChild(l); });
      };
    } catch (_) {}
  }, [publiciteData, galleryImages]);

  // Charger la galerie catégorie depuis ContentTemplate public
  useEffect(() => {
    let alive = true;
    setGalleryLoading(true);
    (async () => {
      try {
        const catId = publiciteData?.categorie?._id || publiciteData?.categorie || null;
        if (!catId) { if (alive) setGalleryLoading(false); return; }
        const res = await fetch(`${CONFIG.PATH_API}/api/content/templates?type=category`);
        if (!res.ok) return;
        const json = await res.json();
        const templates = Array.isArray(json.data) ? json.data : [];
        const tpl = templates.find(t => String(t?.categorie?._id || t?.categorie) === String(catId));
        const gal = tpl?.texte?.galerie || {};
        const imgs = Array.isArray(gal.images) ? gal.images : [];
        const descs = Array.isArray(gal.descriptions) ? gal.descriptions : [];
        if (alive) {
          setGalleryImages(imgs);
          setGalleryDescriptions(descs);
        }
        if (alive) setGalleryLoading(false);
      } catch (e) {
        if (alive) setGalleryLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [publiciteData]);

  useEffect(() => {
    try {
      if (!galleryImages || galleryImages.length === 0) return;
      const count = Math.max(slidesPerView, 3);
      const urls = galleryImages
        .slice(0, count)
        .map((u) => resolveImageUrl(u))
        .filter((u) => !!u);

      const links = [];
      urls.forEach((href) => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = href;
        document.head.appendChild(link);
        links.push(link);
      });

      urls.forEach((href) => {
        const img = new Image();
        img.loading = 'eager';
        img.src = href;
      });

      return () => {
        links.forEach((l) => {
          if (l && l.parentNode) l.parentNode.removeChild(l);
        });
      };
    } catch (_) {}
  }, [galleryImages, slidesPerView]);

  // Fonction utilitaire pour nettoyer le texte
  const cleanText = (text) => {
    if (!text || typeof text !== 'string') return '';
    return text
      .replace(/\*\([^)]*\)\*/g, '') // Supprimer annotations SEO
      .replace(/\n\n\([^)]*\)/g, '') // Supprimer annotations en fin
      .replace(/\\n/g, '\n') // Convertir \n en vraies nouvelles lignes
      .trim();
  };

  // Fonction pour formater le texte avec gras
  const formatText = (text) => {
    if (!text || typeof text !== 'string') return '';
    
    let cleanedText = cleanText(text);
    
    // Remplacer **texte** par <strong>texte</strong>
    cleanedText = cleanedText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    cleanedText = cleanedText.replace(/\*(.*?)\*/g, '<strong>$1</strong>');
    
    return cleanedText;
  };

  // Fonction pour afficher un élément avec fallback
  const renderContent = (content, fallback = "") => {
    if (!content) return fallback;
    if (Array.isArray(content) && content.length === 0) return fallback;
    return content;
  };

  // Normaliser les URL d'images (assure que les uploads pointent vers l'API)
  const resolveImageUrl = (url) => {
    if (!url) return '';
    const u = String(url);
    if (u.startsWith('http://') || u.startsWith('https://')) return u;
    if (u.startsWith('/images/')) return u; // asset frontend
    if (u.startsWith('/uploads/')) return `${CONFIG.PATH_API}${u}`;
    if (u.includes('/uploads/')) {
      const norm = u.startsWith('/') ? u : `/${u}`;
      return `${CONFIG.PATH_API}${norm}`;
    }
    return `${CONFIG.PATH_API}/uploads/${u.replace(/^\//, '')}`;
  };

  // Affichage de chargement
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
          <p className="text-xl">Chargement de la page...</p>
          <p className="text-gray-400 mt-2">{locationParam} - {categorieParam}</p>
        </div>
      </div>
    );
  }

  // Affichage d'erreur
  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center text-white max-w-md">
          <div className="text-6xl mb-6">⚠️</div>
          <h1 className="text-2xl font-bold mb-4">Publicité non trouvée</h1>
          <p className="text-gray-300 mb-6">{error}</p>
          <p className="text-sm text-gray-400 mb-8">
            Recherché: {locationParam} - {categorieParam}
          </p>
          <Link 
            to="/" 
            className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary/90 transition-colors"
          >
            Retour à l'accueil
          </Link>
        </div>
      </div>
    );
  }

  // Vérification des données
  if (!publiciteData || !publiciteData.texte) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center text-white">
          <p className="text-xl">Aucune donnée disponible</p>
        </div>
      </div>
    );
  }

  const { texte } = publiciteData;

  return (
    <div className="min-h-screen bg-gray-900">
      
      {/* Hero Section */}
      {(() => {
        const heroBg = resolveImageUrl(texte.hero?.background_image);
        const heroStyle = heroBg ? {
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.4)), url(${heroBg})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat'
        } : {};
  return (
      <section 
        className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-24 overflow-hidden min-h-[80vh] flex items-center"
            style={heroStyle}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/30 to-black/50"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20"></div>
        
        <div className="relative container mx-auto px-4 text-center z-10">
          <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-white via-gray-100 to-gray-300 bg-clip-text text-transparent">
              {cleanText(texte.hero?.titre_principal)}
            </h1>
            
          <p className="text-xl md:text-2xl mb-8 text-gray-200 leading-relaxed max-w-4xl mx-auto">
              {cleanText(texte.hero?.sous_titre)}
            </p>
            
            <div 
            className="text-lg mb-12 max-w-3xl mx-auto text-gray-300 leading-relaxed"
              dangerouslySetInnerHTML={{
                __html: formatText(texte.hero?.description)
              }}
            />
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link
              to={categorySlug ? `/categories/${categorySlug}` : '/services'}
              className="bg-primary text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-primary/90 transition-all duration-300"
            >
                {cleanText(texte.hero?.bouton_principal) || "Devis Gratuit"}
            </Link>
            <Link to="/contact" className="border-2 border-gray-400 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:border-primary hover:bg-primary/20 transition-all duration-300">
                {cleanText(texte.hero?.bouton_secondaire) || "Voir nos Services"}
            </Link>
          </div>
        </div>
      </section>
        );
      })()}

      {/* Section Galerie - SYSTÈME À 3 NIVEAUX */}
      <section className="py-16 bg-gray-800">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-12">
            {cleanText(texte.galerie?.titre) || 'Nos Réalisations'}
          </h2>
          
          {/* IMAGES : viennent du ContentTemplate catégorie */}
          {galleryImages && galleryImages.length > 0 ? (
            <div className="relative max-w-6xl mx-auto">
              <div
                className="overflow-hidden"
              >
                <div
                  className="flex transition-transform duration-500 ease-out"
                  style={{ transform: `translateX(-${currentIndex * (100 / slidesPerView)}%)` }}
                >
                  {galleryImages.map((image, index) => (
                    <div
                      key={index}
                      className="px-3"
                      style={{ minWidth: `calc(${100 / slidesPerView}% - 0px)` }}
                    >
                      <div className="group relative overflow-hidden rounded-xl shadow-lg bg-gray-900">
              <img 
                          src={resolveImageUrl(image)}
                          alt={cleanText(galleryDescriptions?.[index]) || 'Réalisation'}
                          loading={index < Math.max(slidesPerView, 3) ? 'eager' : 'lazy'}
                          fetchpriority={index < Math.max(slidesPerView, 3) ? 'high' : 'auto'}
                          decoding="async"
                          className="w-full h-64 object-cover transition-all duration-300"
                        />
                        <div className="absolute inset-0 bg-black/80 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                          <div className="px-4 text-center">
                            <p className="text-white font-semibold text-base md:text-lg">
                              {galleryDescriptions?.[index] || 'Réalisation'}
                </p>
              </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {(() => {
                const total = galleryImages.length;
                const maxIndex = Math.max(0, total - slidesPerView);
                const canPrev = currentIndex > 0;
                const canNext = currentIndex < maxIndex;
                return (
                  <div className="mt-6 flex items-center justify-between">
                    <button
                      type="button"
                      aria-label="Précédent"
                      onClick={() => canPrev && setCurrentIndex((i) => Math.max(0, i - 1))}
                      className={`flex items-center justify-center w-10 h-10 rounded-full border border-gray-600 text-white hover:bg-gray-700 transition ${canPrev ? '' : 'opacity-50 cursor-not-allowed'}`}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <div className="text-sm text-gray-300">
                      {Math.min(currentIndex + slidesPerView, total)} / {total}
                    </div>
                    <button
                      type="button"
                      aria-label="Suivant"
                      onClick={() => canNext && setCurrentIndex((i) => Math.min(maxIndex, i + 1))}
                      className={`flex items-center justify-center w-10 h-10 rounded-full border border-gray-600 text-white hover:bg-gray-700 transition ${canNext ? '' : 'opacity-50 cursor-not-allowed'}`}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                );
              })()}
            </div>
          ) : (
            galleryLoading ? (
              <div className="relative max-w-6xl mx-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 animate-pulse">
                  {Array.from({ length: Math.max(slidesPerView, 3) }).map((_, i) => (
                    <div key={i} className="rounded-xl bg-gray-700/50 h-64"></div>
                  ))}
                    </div>
                <div className="flex items-center justify-center mt-6 text-gray-300">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-2 border-gray-400 border-t-transparent"></div>
                  <span className="ml-3">Chargement des réalisations...</span>
                  </div>
                </div>
            ) : (
              <div className="text-center text-gray-400">
                <p className="text-lg">Nos réalisations seront bientôt disponibles</p>
                <p className="text-sm mt-2">Images en cours d'ajout dans notre base de données</p>
                {galleryDescriptions && galleryDescriptions.length > 0 && (
                  <div className="mt-8 max-w-2xl mx-auto">
                    <p className="text-white mb-4">Nos prochaines réalisations :</p>
                    <ul className="text-left space-y-2">
                      {galleryDescriptions.map((desc, index) => (
                        <li key={index} className="text-gray-300">• {desc}</li>
                      ))}
                    </ul>
            </div>
          )}
            </div>
            )
          )}
        </div>
      </section>

      {/* Section Expertise - SYSTÈME À 3 NIVEAUX */}
      <section className="py-16 bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            {/* TITRE : content_type "unique" = vient de la publicité */}
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {cleanText(texte.expertise?.titre) || "Notre Expertise"}
            </h2>
            {/* DESCRIPTION : content_type "unique" = vient de la publicité */}
            <div 
              className="text-base text-gray-300 max-w-3xl mx-auto leading-relaxed"
              dangerouslySetInnerHTML={{
                __html: formatText(texte.expertise?.description)
              }}
            />
          </div>

          {/* STATS : content_type "overall" = viennent du ContentTemplate overall */}
          {texte.expertise?.stats && texte.expertise.stats.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {texte.expertise.stats.map((stat, index) => (
                <div key={index} className="text-center bg-gray-800 p-6 rounded-xl hover:bg-gray-700 transition-all duration-300 border border-gray-700">
                  <div className="w-12 h-12 mx-auto mb-4 bg-primary/20 rounded-full flex items-center justify-center">
                    <span className="text-2xl">{stat.icon || "✅"}</span>
                  </div>
                  <div className="text-3xl font-bold text-primary mb-2">{stat.number}</div>
                  <div className="text-gray-300">{stat.label}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-400">
              <p>Statistiques en cours de mise à jour</p>
            </div>
          )}
        </div>
      </section>

      {/* Section Spécialités - SYSTÈME À 3 NIVEAUX */}
      <section className="py-16 bg-gray-800">
        <div className="container mx-auto px-4">
          {/* TITRE : content_type "unique" = vient de la publicité */}
          <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-12">
            {cleanText(texte.specialites?.titre) || "Nos Spécialités"}
          </h2>
          
          {/* SERVICES : content_type "catégorie" = viennent du ContentTemplate catégorie */}
          {texte.specialites?.services && texte.specialites.services.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {texte.specialites.services.map((service, index) => (
              <div key={index} className="bg-gray-900 rounded-xl p-6 hover:bg-gray-700 transition-all duration-300 border border-gray-600 group">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1 group-hover:bg-primary/30 transition-colors">
                    <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div className="flex-1">
                      {typeof service === 'object' ? (
                        <div>
                          <h3 className="text-lg font-semibold text-white mb-2">{service.nom}</h3>
                          <p className="text-gray-300 mb-3">{service.description}</p>
                          {service.caracteristiques && (
                            <ul className="text-sm text-gray-400 space-y-1">
                              {service.caracteristiques.map((carac, i) => (
                                <li key={i}>• {carac}</li>
                              ))}
                            </ul>
                          )}
                          {service.prix && (
                            <p className="text-primary font-semibold mt-2">{service.prix}</p>
                          )}
                        </div>
                      ) : (
                    <div 
                      className="text-gray-300 leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: formatText(service) }}
                    />
                      )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          ) : (
            <div className="text-center text-gray-400">
              <p>Spécialités en cours de mise à jour</p>
            </div>
          )}
        </div>
      </section>

      {/* Section Solutions Innovantes - SYSTÈME À 3 NIVEAUX */}
      <section className="py-16 bg-gray-900">
        <div className="container mx-auto px-4">
          {/* TITRE : content_type "unique" = vient de la publicité */}
          <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-12">
            {cleanText(texte.solutions_innovantes?.titre) || "Solutions Innovantes"}
          </h2>
          
          {/* SOLUTIONS : content_type "catégorie" = viennent du ContentTemplate catégorie */}
          {texte.solutions_innovantes?.solutions && texte.solutions_innovantes.solutions.length > 0 ? (
          <div className="grid md:grid-cols-2 gap-8">
              {texte.solutions_innovantes.solutions.map((solution, index) => (
              <div key={index} className="bg-gray-800 rounded-xl p-8 hover:bg-gray-700 transition-all duration-300 border border-gray-600 group">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1 group-hover:bg-primary/30 transition-colors">
                    <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                      {typeof solution === 'object' ? (
                        <div>
                          <h3 className="text-lg font-semibold text-white mb-2">{solution.nom}</h3>
                          <p className="text-gray-300 mb-3">{solution.description}</p>
                          {solution.avantages && (
                            <ul className="text-sm text-gray-400 space-y-1">
                              {solution.avantages.map((avantage, i) => (
                                <li key={i}>• {avantage}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ) : (
                    <div 
                          className="text-gray-300 leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: formatText(solution) }}
                    />
                      )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          ) : (
            <div className="text-center text-gray-400">
              <p>Solutions innovantes en cours de mise à jour</p>
                  </div>
          )}
        </div>
      </section>

      {/* Section Pourquoi nous choisir - SYSTÈME À 3 NIVEAUX */}
      <section className="py-16 bg-gray-800">
        <div className="container mx-auto px-4">
          {/* TITRE : content_type "unique" = vient de la publicité */}
          <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-12">
            {cleanText(texte.pourquoi_choisir?.titre) || "Pourquoi Nous Choisir ?"}
          </h2>
          
          {/* RAISONS : content_type "overall" = viennent du ContentTemplate overall */}
          {texte.pourquoi_choisir?.raisons && texte.pourquoi_choisir.raisons.length > 0 ? (
            (() => {
              const rs = texte.pourquoi_choisir.raisons;
              if (rs.length <= 4) {
                return (
                  <div className="grid sm:grid-cols-2 gap-6">
                    {rs.map((raison, index) => (
                      <div key={index} className="bg-gray-900 p-6 rounded-xl hover:bg-gray-700 transition-all duration-300 border border-gray-600 group">
                        <div className="flex items-start space-x-4">
                          <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1 group-hover:bg-primary/30 transition-colors">
                            <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                  </div>
                          <div className="flex-1">
                            {typeof raison === 'object' ? (
                              <div>
                                <h3 className="text-lg font-semibold text-white mb-2">{raison.titre}</h3>
                                <p className="text-gray-300">{raison.description}</p>
                </div>
                            ) : (
                              <div className="text-gray-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: formatText(raison) }} />
                            )}
                  </div>
                </div>
                  </div>
                    ))}
                </div>
                );
              }
              return (
                <div className="relative max-w-5xl mx-auto">
                  <div className="overflow-hidden">
                    <div className="flex transition-transform duration-500 ease-out" style={{ transform: `translateX(-${reasonsIndex * (100 / reasonsSlidesPerView)}%)` }}>
                      {rs.map((raison, index) => (
                        <div key={index} className="px-3" style={{ minWidth: `calc(${100 / reasonsSlidesPerView}% - 0px)` }}>
                          <div className="bg-gray-900 p-6 rounded-xl hover:bg-gray-700 transition-all duration-300 border border-gray-600 group">
                <div className="flex items-start space-x-4">
                              <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1 group-hover:bg-primary/30 transition-colors">
                                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div className="flex-1">
                                {typeof raison === 'object' ? (
                                  <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">{raison.titre}</h3>
                                    <p className="text-gray-300">{raison.description}</p>
                                  </div>
                                ) : (
                                  <div className="text-gray-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: formatText(raison) }} />
                                )}
                              </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
                  </div>
                  {(() => {
                    const total = rs.length;
                    const maxIndex = Math.max(0, total - reasonsSlidesPerView);
                    const canPrev = reasonsIndex > 0;
                    const canNext = reasonsIndex < maxIndex;
                    return (
                      <div className="mt-6 flex items-center justify-between">
                        <button type="button" aria-label="Précédent" onClick={() => canPrev && setReasonsIndex((i) => Math.max(0, i - 1))} className={`flex items-center justify-center w-10 h-10 rounded-full border border-gray-600 text-white hover:bg-gray-700 transition ${canPrev ? '' : 'opacity-50 cursor-not-allowed'}`}>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>
                        <div className="text-sm text-gray-300">{Math.min(reasonsIndex + reasonsSlidesPerView, total)} / {total}</div>
                        <button type="button" aria-label="Suivant" onClick={() => canNext && setReasonsIndex((i) => Math.min(maxIndex, i + 1))} className={`flex items-center justify-center w-10 h-10 rounded-full border border-gray-600 text-white hover:bg-gray-700 transition ${canNext ? '' : 'opacity-50 cursor-not-allowed'}`}>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    );
                  })()}
                </div>
              );
            })()
          ) : (
            <div className="text-center text-gray-400">
              <p>Nos avantages en cours de mise à jour</p>
            </div>
          )}
        </div>
      </section>

      {/* Section Garanties - SYSTÈME À 3 NIVEAUX */}
      <section className="py-16 bg-gray-900">
        <div className="container mx-auto px-4">
          {/* TITRE : content_type "overall" = vient du ContentTemplate overall */}
          <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-12">
            {cleanText(texte.garanties?.titre) || "Nos Garanties"}
          </h2>
          
          {/* LISTE : content_type "overall" = vient du ContentTemplate overall */}
          {texte.garanties?.liste && texte.garanties.liste.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {texte.garanties.liste.map((garantie, index) => (
                <div key={index} className="text-center bg-gray-800 p-6 rounded-xl border border-gray-600 hover:border-primary/50 transition-all duration-300 group">
                  <div className="w-12 h-12 mx-auto mb-4 bg-primary/20 rounded-full flex items-center justify-center group-hover:bg-primary/30 transition-colors">
                    {/* Gérer les objets ET les strings */}
                    <span className="text-2xl">
                      {typeof garantie === 'object' ? (garantie.icon || "🛡️") : "🛡️"}
                    </span>
              </div>
                  {typeof garantie === 'object' ? (
                    <>
                      <h3 className="text-lg font-semibold text-white mb-2">{garantie.nom}</h3>
                      <p className="text-gray-400 text-sm">{garantie.description}</p>
                    </>
                  ) : (
                    <>
                      <h3 className="text-lg font-semibold text-white mb-2">
                        {garantie.split(' ').slice(0, 3).join(' ')}
                      </h3>
                      <p className="text-gray-400 text-sm">{garantie}</p>
                    </>
                  )}
            </div>
              ))}
              </div>
          ) : (
            <div className="text-center text-gray-400">
              <p>Nos garanties en cours de mise à jour</p>
            </div>
          )}
        </div>
      </section>

      {/* Section Zone d'intervention - SYSTÈME À 3 NIVEAUX */}
      <section className="py-16 bg-gray-800">
        <div className="container mx-auto px-4">
          {/* TITRE : content_type "unique" = vient de la publicité */}
          <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-12">
            {cleanText(texte.zone_intervention?.titre) || "Notre Zone d'Intervention"}
          </h2>
          
          {/* QUARTIERS : content_type "unique" = viennent de la publicité */}
          {texte.zone_intervention?.quartiers && texte.zone_intervention.quartiers.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {texte.zone_intervention.quartiers.map((quartier, index) => (
                <div key={index} className="bg-gray-900 p-6 rounded-xl hover:bg-gray-700 transition-all duration-300 border border-gray-600 group">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1 group-hover:bg-primary/30 transition-colors">
                    <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                      {typeof quartier === 'object' ? (
                        <div>
                          <h3 className="text-lg font-semibold text-white mb-2">{quartier.nom}</h3>
                          <p className="text-gray-300">{quartier.description}</p>
                        </div>
                      ) : (
                    <div 
                      className="text-gray-300 leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: formatText(quartier) }}
                    />
                      )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          ) : (
            <div className="text-center text-gray-400">
              <p>Zones d'intervention en cours de mise à jour</p>
            </div>
          )}

          {/* PROXIMITÉ : content_type "unique" = vient de la publicité */}
          {texte.zone_intervention?.proximite && (
            <div className="mt-12 text-center">
              <div 
                className="text-lg text-gray-300 max-w-3xl mx-auto leading-relaxed"
                dangerouslySetInnerHTML={{
                  __html: formatText(texte.zone_intervention.proximite)
                }}
              />
            </div>
          )}
        </div>
      </section>

      {/* Section Processus - SYSTÈME À 3 NIVEAUX */}
      <section className="py-16 bg-gray-900">
        <div className="container mx-auto px-4">
          {/* TITRE : content_type "overall" = vient du ContentTemplate overall */}
          <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-12">
            {cleanText(texte.processus?.titre) || "Notre Processus"}
          </h2>
          
          {/* ÉTAPES : content_type "overall" = viennent du ContentTemplate overall */}
          {texte.processus?.etapes && texte.processus.etapes.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {texte.processus.etapes.map((etape, index) => (
                <div key={index} className="text-center bg-gray-800 p-6 rounded-xl border border-gray-600 hover:border-primary/50 transition-all duration-300 group relative">
                  <div className="w-12 h-12 mx-auto mb-4 bg-primary/20 rounded-full flex items-center justify-center group-hover:bg-primary/30 transition-colors">
                    <span className="text-xl font-bold text-primary">{index + 1}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{etape.nom}</h3>
                  <p className="text-gray-400 text-sm">{etape.description}</p>
                  {index < texte.processus.etapes.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                      <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-400">
              <p>Notre processus en cours de mise à jour</p>
            </div>
          )}
        </div>
      </section>

      {/* Section Témoignages - SYSTÈME À 3 NIVEAUX */}
      <section className="py-16 bg-gray-800">
        <div className="container mx-auto px-4">
          {/* TITRE : content_type "overall" = vient du ContentTemplate overall */}
          <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-12">
            {cleanText(texte.temoignages?.titre) || "Témoignages Clients"}
          </h2>
          
          {/* LISTE : content_type "unique" = vient de la publicité */}
          {texte.temoignages?.liste && texte.temoignages.liste.length > 0 ? (
          <div className="grid md:grid-cols-3 gap-8">
              {texte.temoignages.liste.map((temoignage, index) => (
                <div key={index} className="bg-gray-900 p-8 rounded-xl border border-gray-600">
                  <div className="flex items-center mb-4">
                    <div className="flex text-yellow-400">
                      {[...Array(temoignage.note || 5)].map((_, i) => (
                        <svg key={i} className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                        </svg>
                      ))}
                    </div>
                  </div>
                  <p className="text-gray-300 mb-4">"{cleanText(temoignage.commentaire)}"</p>
                  <div className="text-sm text-gray-400">
                    - {temoignage.nom}
                    {temoignage.ville_affichee && `, ${locationParam}`}
                  </div>
                </div>
                      ))}
                    </div>
          ) : (
            <div className="text-center text-gray-400">
              <p>Témoignages clients en cours de mise à jour</p>
                  </div>
          )}
        </div>
      </section>

      {/* Section Call to Action - SYSTÈME À 3 NIVEAUX */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-primary/10 to-gray-900">
        <div className="container mx-auto px-4 text-center">
          {/* TITRE : content_type "unique" = vient de la publicité */}
          <h2 className="text-3xl md:text-5xl font-bold mb-8 text-white">
            {cleanText(texte.call_to_action?.titre) || "Prêt à Commencer Votre Projet ?"}
          </h2>
          
          {/* DESCRIPTION : content_type "unique" = vient de la publicité */}
          <div 
            className="text-xl mb-12 max-w-3xl mx-auto text-gray-300 leading-relaxed"
            dangerouslySetInnerHTML={{
              __html: formatText(texte.call_to_action?.description)
            }}
          />
          
          {/* BOUTONS : content_type "unique" = viennent de la publicité */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center mb-16">
            {texte.call_to_action?.boutons && texte.call_to_action.boutons.length > 0 ? (
              texte.call_to_action.boutons.map((bouton, index) => (
                <Link 
                  key={index}
                  to={index === 0 ? (categorySlug ? `/categories/${categorySlug}` : '/services') : '/contact'}
                  className={`font-bold text-xl px-12 py-4 rounded-xl transition-all duration-300 ${
                    index === 0 
                      ? "bg-primary text-white hover:bg-primary/90" 
                      : "bg-white text-gray-900 hover:bg-gray-100"
                  }`}
                >
                  {/* Gérer les strings ET les objets */}
                  {typeof bouton === 'object' ? bouton.texte : bouton}
                </Link>
              ))
            ) : (
              <>
            <Link 
              to="/contact"
              className="bg-primary text-white font-bold text-xl px-12 py-4 rounded-xl hover:bg-primary/90 transition-all duration-300"
            >
              Appelez-nous maintenant
            </Link>
            <Link 
              to="/contact"
              className="bg-white text-gray-900 font-bold text-xl px-12 py-4 rounded-xl hover:bg-gray-100 transition-all duration-300"
            >
              Demander un devis
            </Link>
              </>
            )}
          </div>

          {/* GARANTIES : content_type "unique" = viennent de la publicité */}
          <div className={`grid gap-12 text-lg ${
            texte.call_to_action?.garanties && texte.call_to_action.garanties.length > 0
              ? texte.call_to_action.garanties.length === 1 
                ? 'grid-cols-1 justify-items-center' 
                : texte.call_to_action.garanties.length === 2 
                ? 'md:grid-cols-2 justify-items-center max-w-3xl mx-auto' 
                : texte.call_to_action.garanties.length === 3 
                ? 'md:grid-cols-3 justify-items-center max-w-4xl mx-auto' 
                : 'md:grid-cols-4 justify-items-center max-w-5xl mx-auto'
              : 'md:grid-cols-3'
          }`}>
            {texte.call_to_action?.garanties && texte.call_to_action.garanties.length > 0 ? (
              texte.call_to_action.garanties.map((garantie, index) => (
                <div key={index} className="flex items-start justify-center gap-3 text-gray-300">
                  {/* Gérer les strings ET les objets */}
                  {typeof garantie === 'object' ? (
                    <>
                      <span className="text-2xl flex-shrink-0 mt-1">✅</span>
                      <span className="text-center leading-relaxed">{garantie.texte}</span>
                    </>
                  ) : (
                    <>
                      <span className="text-2xl flex-shrink-0 mt-1">✅</span>
                      <span className="text-center leading-relaxed">{garantie}</span>
                    </>
                  )}
                </div>
              ))
            ) : (
              <>
                <div className="flex items-start justify-center gap-3 text-gray-300">
                  <svg className="w-6 h-6 text-primary flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
                  <span className="text-center leading-relaxed">Intervention rapide</span>
            </div>
                <div className="flex items-start justify-center gap-3 text-gray-300">
                  <svg className="w-6 h-6 text-primary flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
                  <span className="text-center leading-relaxed">Devis gratuit</span>
            </div>
                <div className="flex items-start justify-center gap-3 text-gray-300">
                  <svg className="w-6 h-6 text-primary flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
                  <span className="text-center leading-relaxed">Garantie qualité</span>
            </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* FOOTER COMPLET */}
      <footer className="bg-gray-900 border-t border-gray-800">
        <div className="container mx-auto px-4 py-12">
          <div className="grid md:grid-cols-4 gap-8">
            
            {/* Colonne 1 : Logo et description */}
            <div className="md:col-span-1">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center mr-3">
                  <span className="text-white font-bold text-xl">2R</span>
                </div>
                <span className="text-white font-bold text-xl">2RENOV</span>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed mb-4">
                Votre expert en rénovation et travaux à {locationParam}. 
                Spécialiste {categorieParam} avec plus de 15 ans d'expérience.
              </p>
              <div className="flex space-x-4">
                <a href="#" className="text-gray-400 hover:text-primary transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/>
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-primary transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M22.46 6c-.77.35-1.6.58-2.46.69.88-.53 1.56-1.37 1.88-2.38-.83.5-1.75.85-2.72 1.05C18.37 4.5 17.26 4 16 4c-2.35 0-4.27 1.92-4.27 4.29 0 .34.04.67.11.98C8.28 9.09 5.11 7.38 3 4.79c-.37.63-.58 1.37-.58 2.15 0 1.49.75 2.81 1.91 3.56-.71 0-1.37-.2-1.95-.5v.03c0 2.08 1.48 3.82 3.44 4.21a4.22 4.22 0 0 1-1.93.07 4.28 4.28 0 0 0 4 2.98 8.521 8.521 0 0 1-5.33 1.84c-.34 0-.68-.02-1.02-.06C3.44 20.29 5.7 21 8.12 21 16 21 20.33 14.46 20.33 8.79c0-.19 0-.37-.01-.56.84-.6 1.56-1.36 2.14-2.23z"/>
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-primary transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.174-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.402.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.357-.629-2.746-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24.009 12.017 24.009c6.624 0 11.99-5.367 11.99-11.988C24.007 5.367 18.641.001 12.017.001z"/>
                  </svg>
                </a>
              </div>
            </div>

            {/* Colonne 2 : Services */}
            <div>
              <h4 className="text-white font-semibold mb-4">Nos Services</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/services/peinture" className="text-gray-400 hover:text-primary transition-colors">Peinture</Link></li>
                <li><Link to="/services/plomberie" className="text-gray-400 hover:text-primary transition-colors">Plomberie</Link></li>
                <li><Link to="/services/electricite" className="text-gray-400 hover:text-primary transition-colors">Électricité</Link></li>
                <li><Link to="/services/renovation" className="text-gray-400 hover:text-primary transition-colors">Rénovation</Link></li>
                <li><Link to="/services/isolation" className="text-gray-400 hover:text-primary transition-colors">Isolation</Link></li>
              </ul>
            </div>

            {/* Colonne 3 : Zones d'intervention */}
            <div>
              <h4 className="text-white font-semibold mb-4">Zones d'intervention</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/ville/paris" className="text-gray-400 hover:text-primary transition-colors">Paris</Link></li>
                <li><Link to="/ville/meaux" className="text-gray-400 hover:text-primary transition-colors">Meaux</Link></li>
                <li><Link to="/ville/melun" className="text-gray-400 hover:text-primary transition-colors">Melun</Link></li>
                <li><Link to="/departement/seine-et-marne" className="text-gray-400 hover:text-primary transition-colors">Seine-et-Marne</Link></li>
                <li><Link to="/departement/val-de-marne" className="text-gray-400 hover:text-primary transition-colors">Val-de-Marne</Link></li>
              </ul>
            </div>

            {/* Colonne 4 : Contact */}
            <div>
              <h4 className="text-white font-semibold mb-4">Contact</h4>
              <div className="space-y-3 text-sm">
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-primary mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span className="text-gray-400">01 23 45 67 89</span>
                </div>
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-primary mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span className="text-gray-400">contact@2renov.fr</span>
                </div>
                <div className="flex items-start">
                  <svg className="w-4 h-4 text-primary mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="text-gray-400">123 Rue de la Rénovation<br />77000 Melun</span>
                </div>
              </div>
              
              <div className="mt-6">
                <Link 
                  to="/contact" 
                  className="inline-block bg-primary text-white px-6 py-2 rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors"
                >
                  Devis Gratuit
                </Link>
              </div>
            </div>
          </div>

          {/* Séparateur */}
          <div className="border-t border-gray-800 mt-8 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="text-gray-400 text-sm mb-4 md:mb-0">
                © 2024 2RENOV. Tous droits réservés.
              </div>
              <div className="flex space-x-6 text-sm">
                <Link to="/mentions-legales" className="text-gray-400 hover:text-primary transition-colors">
                  Mentions légales
                </Link>
                <Link to="/politique-confidentialite" className="text-gray-400 hover:text-primary transition-colors">
                  Politique de confidentialité
                </Link>
                <Link to="/cgv" className="text-gray-400 hover:text-primary transition-colors">
                  CGV
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Publicite;
